/**
 * src/components/spatial/eventFormModel.ts
 *
 * What a form asks for, in the order somebody would answer it.
 *
 * The requirement is that a person can put on an event without
 * knowing what a contract is. That is a layout problem before it is a
 * permissions problem: the fields that describe an occasion — what,
 * when, where, who — are a complete and useful form on their own, and
 * everything to do with machinery is additive.
 *
 * The server already draws that line. `eventModel.BASIC_FIELDS` is
 * ordered "as a form would ask for them" and `ADVANCED_FIELDS` is
 * everything that only matters once an event is doing something
 * on-chain. This file is the form built from that split, so the two
 * cannot drift into different ideas of what "basic" means — and a test
 * compares the lists rather than trusting the comment.
 *
 * On hiding things. Advanced sections are collapsed and some are
 * marked as needing the venue's agreement, and neither of those is a
 * permission check. The server authorizes every write against the
 * event's authority model; this only saves somebody from filling in a
 * form that was always going to be refused. A UI that treated a
 * collapsed section as a locked door would be a UI with no lock.
 */

import type { EventRecord } from '../../services/spatial/spatialEventsClient';

export type EventFieldKind =
  | 'text' | 'textarea' | 'datetime' | 'timezone' | 'select'
  | 'number' | 'tags' | 'image' | 'object_ref' | 'event_ref' | 'rule_ref' | 'contract_ref';

export interface EventFieldSpec {
  /** The key on the event record. Matches the server's field names. */
  name: keyof EventRecord | 'geometry';
  label: string;
  kind: EventFieldKind;
  /** Asked for before the event can be published. */
  required?: boolean;
  /** A line of help, for a field whose meaning is not obvious. */
  hint?: string;
  options?: readonly string[];
  /**
   * Whether changing this reaches past the event into the venue.
   *
   * Contracts, rules, a parent event and borrowed objects all point
   * machinery at property the event does not own, so the server
   * additionally requires the host's authority. Flagged so the form
   * can say so up front.
   */
  needsHostAuthority?: boolean;
}

export interface EventFieldGroup {
  id: string;
  title: string;
  /** Why this group exists, in one line. */
  blurb: string;
  /** Open by default, or folded away until asked for. */
  openByDefault: boolean;
  fields: readonly EventFieldSpec[];
}

/** Mirrors the server's visibility vocabulary. */
export const EVENT_VISIBILITY = ['public', 'unlisted', 'private'] as const;

/**
 * The basic form.
 *
 * Complete on its own: an event created from only these fields is a
 * real, discoverable, attendable event. Ordered the way the server
 * orders `BASIC_FIELDS`, with the two obvious additions a person
 * expects and the server keeps in its advanced half for storage
 * reasons rather than for difficulty — capacity is a number on a door
 * and an image is a picture, and neither needs explaining.
 */
export const BASIC_GROUPS: readonly EventFieldGroup[] = Object.freeze([
  Object.freeze({
    id: 'what',
    title: 'What is it?',
    blurb: 'The part people read first.',
    openByDefault: true,
    fields: Object.freeze([
      { name: 'title', label: 'Name', kind: 'text', required: true } as EventFieldSpec,
      { name: 'description', label: 'Description', kind: 'textarea' } as EventFieldSpec,
      { name: 'image', label: 'Image', kind: 'image' } as EventFieldSpec,
      {
        name: 'tags', label: 'Tags', kind: 'tags',
        hint: 'How people searching will find it.',
      } as EventFieldSpec,
      { name: 'organizer', label: 'Organizer', kind: 'text' } as EventFieldSpec,
    ]),
  }),
  Object.freeze({
    id: 'when',
    title: 'When is it?',
    blurb: 'The times the map and the schedule follow.',
    openByDefault: true,
    fields: Object.freeze([
      { name: 'startsAt', label: 'Starts', kind: 'datetime', required: true } as EventFieldSpec,
      {
        name: 'endsAt', label: 'Ends', kind: 'datetime',
        hint: 'Optional. Without it the event is treated as running for a few hours.',
      } as EventFieldSpec,
      {
        name: 'timezone', label: 'Time zone', kind: 'timezone',
        hint: 'Defaults to yours.',
      } as EventFieldSpec,
    ]),
  }),
  Object.freeze({
    id: 'where',
    title: 'Where is it?',
    blurb: 'Inside the venue you are creating it at.',
    openByDefault: true,
    fields: Object.freeze([
      {
        name: 'venue', label: 'Venue name', kind: 'text',
        hint: 'What to call the place, if not the building\u2019s own name.',
      } as EventFieldSpec,
      {
        name: 'floorRef', label: 'Floor', kind: 'text',
        hint: 'Optional, for a venue with more than one.',
      } as EventFieldSpec,
      {
        name: 'zoneRef', label: 'Zone or room', kind: 'text',
        hint: 'Optional. A hall, a stage, a courtyard.',
      } as EventFieldSpec,
    ]),
  }),
  Object.freeze({
    id: 'who',
    title: 'Who can come?',
    blurb: 'Who sees it, and how many fit.',
    openByDefault: true,
    fields: Object.freeze([
      {
        name: 'visibility', label: 'Visibility', kind: 'select',
        options: EVENT_VISIBILITY,
        hint: 'Unlisted events are reachable by link but are not browsable.',
      } as EventFieldSpec,
      {
        name: 'capacity', label: 'Capacity', kind: 'number',
        hint: 'Optional. Leave empty for no limit.',
      } as EventFieldSpec,
    ]),
  }),
]);

/**
 * The advanced form.
 *
 * Folded away, and every group says what it is for before it says what
 * it needs — somebody who does not recognise the words should be able
 * to tell from the blurb whether they need to open it.
 */
export const ADVANCED_GROUPS: readonly EventFieldGroup[] = Object.freeze([
  Object.freeze({
    id: 'structure',
    title: 'Part of a bigger event',
    blurb: 'For a talk inside a conference, or a set inside a festival.',
    openByDefault: false,
    fields: Object.freeze([
      {
        name: 'parentEventId', label: 'Parent event', kind: 'event_ref',
        needsHostAuthority: true,
        hint: 'The event this one sits inside.',
      } as EventFieldSpec,
    ]),
  }),
  Object.freeze({
    id: 'spaces',
    title: 'Other spaces it uses',
    blurb: 'A stage, a gate, a bar — objects beyond the venue itself.',
    openByDefault: false,
    fields: Object.freeze([
      {
        name: 'objectIds', label: 'Attached objects', kind: 'object_ref',
        needsHostAuthority: true,
        hint: 'Each one needs the agreement of whoever owns it.',
      } as EventFieldSpec,
    ]),
  }),
  Object.freeze({
    id: 'machinery',
    title: 'Payments and automatic actions',
    blurb: 'Only needed if the event charges for something, or runs something on-chain.',
    openByDefault: false,
    fields: Object.freeze([
      {
        name: 'contractId', label: 'Contract', kind: 'contract_ref',
        needsHostAuthority: true,
        hint: 'What an action at this event calls.',
      } as EventFieldSpec,
      {
        name: 'contractFunction', label: 'Function', kind: 'text',
        needsHostAuthority: true,
      } as EventFieldSpec,
      {
        name: 'executionRuleId', label: 'Execution rule', kind: 'rule_ref',
        needsHostAuthority: true,
        hint: 'When and where the action is allowed to run.',
      } as EventFieldSpec,
    ]),
  }),
  Object.freeze({
    id: 'broadcast',
    title: 'Broadcast',
    blurb: 'What plays on the venue\u2019s surfaces when the event opens.',
    openByDefault: false,
    fields: Object.freeze([
      {
        name: 'contentRef', label: 'Content to show', kind: 'text',
        hint: 'Must be something the venue has already approved.',
      } as EventFieldSpec,
    ]),
  }),
]);

/** Every field name the form can send. Nothing else is submitted. */
export const FORM_FIELDS: readonly string[] = Object.freeze(
  [...BASIC_GROUPS, ...ADVANCED_GROUPS]
    .flatMap((group) => group.fields.map((field) => String(field.name))),
);

/** Field names whose write additionally needs the venue's authority. */
export const HOST_AUTHORITY_FORM_FIELDS: readonly string[] = Object.freeze(
  [...BASIC_GROUPS, ...ADVANCED_GROUPS]
    .flatMap((group) => group.fields)
    .filter((field) => field.needsHostAuthority)
    .map((field) => String(field.name)),
);

// ── Validation ──────────────────────────────────────────────────────

export interface FormProblem {
  field: string | null;
  message: string;
}

/**
 * The same three things the server checks before publishing.
 *
 * Checked here as well so a person hears about a missing title while
 * they are still looking at the field, rather than after a round trip.
 * This is not a substitute for the server's check — the server runs it
 * regardless, and if these two ever disagree the server is right.
 */
export function validateEventForm(
  draft: Partial<EventRecord>,
  { publishing = true }: { publishing?: boolean } = {},
): FormProblem[] {
  const problems: FormProblem[] = [];
  const title = typeof draft.title === 'string' ? draft.title.trim() : '';

  // A draft only needs a name, matching what the server accepts for
  // one. Requiring a time to save a rough idea is how a form stops
  // being useful for planning.
  if (!title) problems.push({ field: 'title', message: 'Give it a name' });
  if (!publishing) return problems;

  if (!draft.startsAt) {
    problems.push({ field: 'startsAt', message: 'Say when it starts' });
  }

  if (draft.startsAt && draft.endsAt) {
    const from = Date.parse(String(draft.startsAt));
    const to = Date.parse(String(draft.endsAt));
    if (Number.isFinite(from) && Number.isFinite(to) && to <= from) {
      problems.push({ field: 'endsAt', message: 'The end has to be after the start' });
    }
  }

  if (draft.capacity != null && draft.capacity !== ('' as unknown)) {
    const capacity = Number(draft.capacity);
    if (!Number.isInteger(capacity) || capacity < 0) {
      problems.push({ field: 'capacity', message: 'Capacity has to be a whole number' });
    }
  }

  if (draft.visibility && !EVENT_VISIBILITY.includes(draft.visibility as never)) {
    problems.push({ field: 'visibility', message: 'Pick a visibility' });
  }

  return problems;
}

/**
 * Keep only what the form is allowed to send.
 *
 * Built by picking from the known field list rather than by deleting
 * from the draft, so a stray key added to component state tomorrow is
 * not quietly submitted. The same shape the server route uses for the
 * same reason.
 */
export function eventPatchFrom(draft: Record<string, unknown>): Partial<EventRecord> {
  const patch: Record<string, unknown> = {};
  for (const field of FORM_FIELDS) {
    if (!Object.prototype.hasOwnProperty.call(draft, field)) continue;
    const value = draft[field];
    // An empty string is a cleared field, not the text "". Sent as
    // null so the server's normalizer drops it rather than storing a
    // blank that then displays as a blank.
    patch[field] = value === '' ? null : value;
  }
  return patch as Partial<EventRecord>;
}

/**
 * Whether a draft has anything in its advanced half.
 *
 * Used to open the advanced section for an event that already has
 * machinery — hiding a contract somebody attached last week behind a
 * fold they have to remember to open is how configuration gets lost.
 */
export function draftUsesAdvanced(draft: Partial<EventRecord> | null | undefined): boolean {
  if (!draft) return false;
  for (const group of ADVANCED_GROUPS) {
    for (const field of group.fields) {
      const value = (draft as Record<string, unknown>)[String(field.name)];
      if (value == null || value === '') continue;
      if (Array.isArray(value) && value.length === 0) continue;
      return true;
    }
  }
  return false;
}
